import { z } from "zod";
import {
  ApiError,
  backendFetchResponse,
  parseResponse,
} from "@softmax/observatory-client/transport";
import type { ReplayReadiness } from "./replay-session-records";

const replaySessionRetryMs = 1000;
const readinessSchema = z.object({ ready: z.boolean().optional() });

async function rejectHealthResponse(response: Response): Promise<never> {
  if (
    response.headers
      .get("content-type")
      ?.toLowerCase()
      .includes("application/json")
  )
    return parseResponse<never>(response);

  throw new ApiError(
    (await response.text()) || "Replay session failed to start",
    response.status,
    undefined,
    undefined,
    response,
  );
}

export async function waitForReplayReadiness({
  viewerUrl,
  signal,
  elevatedPrivileges,
  onHealthPoll,
}: Readonly<{
  viewerUrl: string;
  signal: AbortSignal;
  elevatedPrivileges: boolean;
  onHealthPoll?: ((healthPollCount: number) => void) | undefined;
}>): Promise<Omit<ReplayReadiness, "id">> {
  const viewer = new URL(viewerUrl);
  const proxyIndex = viewer.pathname.indexOf("/proxy/");

  if (proxyIndex < 0 || !viewer.pathname.startsWith("/v2/"))
    throw new Error("Replay session URL is missing an Observatory proxy route");

  const healthUrl = `/api/observatory${viewer.pathname.slice(0, proxyIndex)}/proxy/healthz`;
  let healthPollCount = 0;
  const headers: Record<string, string> = {};

  if (elevatedPrivileges) headers["X-Use-Elevated-Privileges"] = "true";

  while (true) {
    signal.throwIfAborted();
    healthPollCount += 1;
    onHealthPoll?.(healthPollCount);
    const response = await backendFetchResponse("", healthUrl, {
      headers,
      signal,
      // The readiness collection owns one deadline across all health polls.
      timeoutMs: null,
    });

    if (response.ok) {
      const body = readinessSchema.parse(await response.json());
      signal.throwIfAborted();

      if (body.ready) return { readyAt: performance.now(), healthPollCount };
    } else if (response.status === 503) {
      await response.body?.cancel();
    } else {
      return rejectHealthResponse(response);
    }

    await new Promise<void>((resolve, reject) => {
      signal.throwIfAborted();
      const cancel = () => {
        clearTimeout(timer);
        reject(signal.reason);
      };
      const timer = setTimeout(() => {
        signal.removeEventListener("abort", cancel);
        resolve();
      }, replaySessionRetryMs);
      signal.addEventListener("abort", cancel, { once: true });
    });
  }
}
